import { Typography as MuiTypography, Divider } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import InflowScatterChartContainer from "../../components/charts/inflowScatterChart/InflowScatterChartContainer";
import RawTable from "../../components/RawTable";
import { getWaterYearRange } from "../fieldDepthProfile/FieldDepthProfile";
import useDropdown from "../../hooks/useDropdown";
import React, { useMemo } from "react";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const StyledDivider = styled(Divider)(spacing);

const { start_date, end_date } = getWaterYearRange();

const PARAMETER_GROUPS = [
  {
    label: "Nitrogen Concentrations",
    value: [29, 45], // total and dissolved
  },
  {
    label: "Phosphorus Concentrations",
    value: [28, 47],
  },
  {
    label: "Total Suspended Solids (TSS)",
    value: [30],
  },
];

// const LOCATION_OPTIONS = [
//   { label: "CC-10", value: "68" },
//   { label: "CT-2", value: "83" },
// ];

const DEFAULTS = {
  parameterGroup: [29, 45],
  start_date,
  end_date,
  locationIds: ["68", "83"],
};

const schema = z.object({
  parameterGroup: z.preprocess(
    (val) => (typeof val === "string" ? val.split(",").map(Number) : val),
    z.array(z.number()).default(DEFAULTS.parameterGroup)
  ),
  start_date: z.string().default(DEFAULTS.start_date),
  end_date: z.string().default(DEFAULTS.end_date),
  locationIds: z.preprocess(
    (v) => (typeof v === "string" ? v.split(",") : v),
    z.array(z.string()).default(DEFAULTS.locationIds)
  ),
});

export default function CcrInflowConcentrations() {
  const { params, setParams } = useQueryParams(schema);

  const locOptions = useDropdown(
    "results-simple/download-dropdown-locs-active"
  );

  const COLUMNS = [
    { title: "Date", field: "rdate", type: "date" },
    { title: "Time", field: "rtime", type: "time" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "parameter" },
    { title: "Value", field: "rvalue_converted", type: "numeric" },
    { title: "Unit", field: "unit_name" },
  ];

  const FILTERS = useMemo(
    () => [
      {
        type: "select",
        param: "parameterGroup",
        label: "Parameter Group",
        default: params.parameterGroup.map(String),
        options: PARAMETER_GROUPS.map((group) => ({
          label: group.label,
          value: group.value.map(String).join(","),
        })),
      },
      {
        type: "date",
        param: "start_date",
        label: "Start Date",
        default: params.start_date,
      },
      {
        type: "date",
        param: "end_date",
        label: "End Date",
        default: params.end_date,
      },
      {
        type: "auto",
        multi: true,
        param: "locationIds",
        label: "Locations",
        default: params.locationIds.map(String),
        options: locOptions,
      },
    ],
    [locOptions, params]
  );

  return (
    <>
      <Helmet title="Watershed Water Quality" />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Watershed Water Quality
        </Typography>
        <StyledDivider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This page explores nutrient and sediment concentrations in the two
            main reservoir inflows: <strong>Cherry Creek (CC-10)</strong> and{" "}
            <strong>Cottonwood Creek (CT-2)</strong>. These inflows contribute
            to overall reservoir water quality and are monitored for trends in{" "}
            <strong>Total Nitrogen</strong>,{" "}
            <strong>Total and Dissolved Phosphorus</strong>, and{" "}
            <strong>Total Suspended Solids (TSS)</strong>.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Use the filters to compare different <strong>inflow sites</strong>{" "}
            and <strong>parameter groups</strong> over time. This view helps
            assess whether nutrient levels entering the reservoir are changing
            across seasons or years.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the chart and table
            update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS} onChange={setParams} />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Watershed Water Quality
          </Typography>
          <InflowScatterChartContainer
            parameterGroup={params.parameterGroup}
            startDate={params.start_date}
            endDate={params.end_date}
            locationIds={params.locationIds}
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>

          <Typography variant="body1" gutterBottom>
            This scatterplot shows individual sampling results for selected
            inflow locations and parameters over time. Each point represents a
            single sampling event, with the <strong>date</strong> on the x-axis
            and the <strong>concentration</strong> (in mg/L) on the y-axis.
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Colors</strong> distinguish sampling locations, while{" "}
            <strong>filled vs. hollow circles</strong> indicate whether the
            result is for a <strong>total</strong> or <strong>dissolved</strong>{" "}
            measurement of the selected parameter group.
          </Typography>

          <Typography variant="body1" gutterBottom>
            A vertical line follows your cursor across the chart to help
            identify coinciding sampling dates across locations and parameter
            types.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Use the filters above to change the <strong>parameter group</strong>
            , <strong>locations</strong>, and <strong>date range</strong> shown
            in the graph and table.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>
          <RawTable
            endPoint="results-simple/ccr-inflow"
            params={{
              parameterGroup: params.parameterGroup.join(","),
              start_date: params.start_date,
              end_date: params.end_date,
              locationIds: params.locationIds.join(","),
            }}
            columns={COLUMNS}
          />
        </Section>
      </main>
    </>
  );
}
